import { useQuery } from '@hooks/reactQuery';

import { getOrganizationDashboardAssetTypesSummary } from '@api/dashboardPO';

import { extractQueryParams } from '@utils/common';

import { IId } from '@interfaces/common/common';
import { TSortedDataParams } from '@interfaces/common/response';
import { ISort } from '@interfaces/common/store';
import { IAssetTypesOrganizationDashboard } from '@interfaces/platform/platformDashboard';

import { refetchOptions } from '@constants/common';
import { DASHBOARD_ASSET_TYPES_SUMMARY_ORGANIZATION } from '@constants/queryKeys';

const getSortedData = ({
	sortDirection,
	sortFieldName,
	data,
}: TSortedDataParams<IAssetTypesOrganizationDashboard>) => {
	if (!data) return [];

	if (!sortFieldName) return data.filter(item => !!item);

	return [...data].sort((a, b) => {
		const [first, second] = sortDirection ? [a, b] : [b, a];

		switch (sortFieldName) {
			case 'assetTypeName':
				return first.type.assetTypeName.localeCompare(second.type.assetTypeName);
			case 'allAssets':
				return first.allAssets - second.allAssets;
			case 'showedAssets':
				return first.showedAssets - second.showedAssets;
			case 'hiddenAssets':
				return first.hiddenAssets - second.hiddenAssets;
			default:
				return 0;
		}
	}).filter(item => !!item);
};

type TUseQueryOrganizationDashboardAssetTypesSummary = ISort & IId<string | undefined>

const useQueryOrganizationDashboardAssetTypesSummary = ({
	id,
	sortDirection,
	sortFieldName,
}: TUseQueryOrganizationDashboardAssetTypesSummary) => {
	const {
		data,
		isLoading,
		isFetching,
	} = useQuery({
		queryKey: [DASHBOARD_ASSET_TYPES_SUMMARY_ORGANIZATION, id],
		queryFn: extractQueryParams(getOrganizationDashboardAssetTypesSummary),
		enabled: [!!id],
		...refetchOptions,
	});

	const organizationDashboardAssetTypesSummary = getSortedData({
		sortDirection,
		sortFieldName,
		data,
	});

	return {
		organizationDashboardAssetTypesSummary,
		isLoadingOrganizationDashboardAssetTypesSummary: (isLoading && !!organizationDashboardAssetTypesSummary)
			|| isFetching,
	};
};

export default useQueryOrganizationDashboardAssetTypesSummary;
